import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Grid,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Save, ArrowLeft, ClipboardCopy } from 'lucide-react';
import { CompanyFormData } from '../../types/company';
import { TaxRegimeOption } from '../../types/taxRegime';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { CompanyService } from '../../services/companyService';
import TaxRegimeService from '../../services/taxRegimeService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

// Validar CNPJ (função auxiliar simples - pode ser melhorada)
const isValidCNPJ = (cnpj: string | null | undefined): boolean => {
  if (!cnpj) return false;
  cnpj = cnpj.replace(/[^\d]+/g,'');
  if(cnpj == '') return false;
  if (cnpj.length != 14) return false;
  // Elimina CNPJs invalidos conhecidos
  if (cnpj == "00000000000000" || 
      cnpj == "11111111111111" || 
      cnpj == "22222222222222" || 
      cnpj == "33333333333333" || 
      cnpj == "44444444444444" || 
      cnpj == "55555555555555" || 
      cnpj == "66666666666666" || 
      cnpj == "77777777777777" || 
      cnpj == "88888888888888" || 
      cnpj == "99999999999999")
      return false;
        
  // Valida DVs
  let tamanho = cnpj.length - 2
  let numeros = cnpj.substring(0,tamanho);
  let digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2)
          pos = 9;
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado != parseInt(digitos.charAt(0)))
      return false;
        
  tamanho = tamanho + 1;
  numeros = cnpj.substring(0,tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2)
          pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado != parseInt(digitos.charAt(1)))
        return false;
          
  return true;
}

const CompanyFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const companyService = useMemo(() => new CompanyService(), []);
  const taxRegimeService = useMemo(() => new TaxRegimeService(), []);
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [taxRegimeOptions, setTaxRegimeOptions] = useState<TaxRegimeOption[]>([]);
  const [taxRegimeError, setTaxRegimeError] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { 
    control, 
    handleSubmit, 
    reset, 
    getValues,
    formState: { errors, isSubmitting } 
  } = useForm<CompanyFormData>({
    defaultValues: {
      cnpj: '',
      name: '',
      fantasy_name: '',
      active: true,
      tax_regime: '',
      bank_pix: '',
      bank_code: '',
      bank_agency: '',
      bank_account: '',
    }
  });

  useEffect(() => {
    setTaxRegimeError(null);
    taxRegimeService.getTaxRegimes()
      .then(options => {
        setTaxRegimeOptions(options);
      })
      .catch(err => {
        console.error("Erro ao buscar regimes tributários:", err);
        setTaxRegimeError(err.message || 'Falha ao carregar regimes tributários.');
        setTaxRegimeOptions([]);
      });
  }, [taxRegimeService]);

  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      companyService.getById(parseInt(id, 10))
        .then(company => {
          reset(company);
          setError(null);
        })
        .catch(err => {
          console.error("Erro ao buscar empresa para edição:", err);
          setError(err.message || 'Falha ao carregar dados da empresa.');
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [id, isEditMode, reset, companyService]);

  const handleConfirmSave: SubmitHandler<CompanyFormData> = async (data) => {
    setIsConfirmDialogOpen(false);
    setLoading(true);
    setError(null);
    
    try {
      const dataToSubmit = {
        ...data,
        cnpj: data.cnpj?.replace(/\D/g, ''),
      };

      let successMessage = '';
      if (isEditMode && id) {
        await companyService.update(parseInt(id, 10), dataToSubmit);
        successMessage = 'Empresa atualizada com sucesso.';
      } else {
        await companyService.create(dataToSubmit);
        successMessage = 'Empresa criada com sucesso.';
      }

      setSnackbarMessage(successMessage);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/empresas');
      }, 1500);

    } catch (err: any) {
      console.error("Erro ao salvar empresa:", err);
      setError(err.message || 'Erro ao salvar empresa. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = () => {
    setIsConfirmDialogOpen(true);
  };

  const handleCancelSave = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    navigate('/empresas');
  };

  const handleCopyPix = () => {
    const pixKey = getValues('bank_pix');
    if (pixKey) {
      navigator.clipboard.writeText(pixKey)
        .then(() => {
          alert('Chave PIX copiada para a área de transferência!');
        })
        .catch(err => {
          console.error('Erro ao copiar Chave PIX: ', err);
          alert('Erro ao copiar Chave PIX.');
        });
    } else {
      alert('Nenhuma Chave PIX para copiar.');
    }
  };

  if (initialLoading && isEditMode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEditMode ? 'Editar Empresa' : 'Nova Empresa'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      {taxRegimeError && <Alert severity="warning" sx={{ mb: 2 }}>{taxRegimeError}</Alert>}

      <Card elevation={2}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={5}>
                <FormControl fullWidth error={!!errors.cnpj}>
                  <Controller
                    name="cnpj"
                    control={control}
                    rules={{ 
                      required: 'CNPJ é obrigatório',
                      validate: value => isValidCNPJ(value) || 'CNPJ inválido'
                    }}
                    render={({ field }) => (
                      <InputMask
                        mask="99.999.999/9999-99"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={loading || isSubmitting}
                      >
                        {/* @ts-ignore */}
                        {(inputProps) => (
                          <TextField
                            {...inputProps}
                            label="CNPJ"
                            variant="outlined"
                            required
                            error={!!errors.cnpj}
                            helperText={errors.cnpj?.message}
                            disabled={loading || isSubmitting}
                            fullWidth
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                              '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                              '& .MuiFormHelperText-root': { fontSize: '0.75rem', marginTop: '2px' }
                            }}
                          />
                        )}
                      </InputMask>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControlLabel
                  control={
                    <Controller
                      name="active"
                      control={control}
                      render={({ field }) => (
                        <Switch 
                          {...field} 
                          checked={field.value ?? false}
                          disabled={loading || isSubmitting}
                        />
                      )}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Ativo</Typography>}
                  sx={{ pt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Razão Social é obrigatória' }}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <TextField
                      {...restField}
                      value={value || ''}
                      onChange={(e) => {
                        const upperCaseValue = e.target.value.toUpperCase();
                        onChange(upperCaseValue);
                      }}
                      label="Razão Social"
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={loading || isSubmitting}
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                        '& .MuiFormHelperText-root': { fontSize: '0.75rem', marginTop: '2px' }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="fantasy_name"
                  control={control}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <TextField
                      {...restField}
                      value={value || ''}
                      onChange={(e) => {
                        const upperCaseValue = e.target.value.toUpperCase();
                        onChange(upperCaseValue);
                      }}
                      label="Nome Fantasia"
                      variant="outlined"
                      fullWidth
                      disabled={loading || isSubmitting}
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="tax_regime"
                  control={control}
                  rules={{ required: 'Regime Tributário é obrigatório' }}
                  render={({ field }) => (
                    <FormControl 
                      fullWidth 
                      required 
                      error={!!errors.tax_regime || !!taxRegimeError}
                      size="small"
                      disabled={taxRegimeOptions.length === 0}
                    >
                      <InputLabel id="tax-regime-label" sx={{ fontSize: '0.85rem' }}>Regime Tributário</InputLabel>
                      <Select
                        {...field}
                        labelId="tax-regime-label"
                        label="Regime Tributário"
                        disabled={loading || isSubmitting || taxRegimeOptions.length === 0}
                        sx={{ height: 40, fontSize: '0.85rem' }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>Selecione...</em></MenuItem>
                        {taxRegimeOptions.map((reg) => (
                          <MenuItem key={reg.key} value={reg.key} sx={{ fontSize: '0.85rem' }}>
                            {reg.value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.tax_regime && <Typography color="error" variant="caption" sx={{ fontSize: '0.75rem', marginTop: '2px' }}>{errors.tax_regime.message}</Typography>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 0.5, mt: 1 }}>
                  Dados Bancários (Opcional)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="bank_pix"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Chave PIX"
                      variant="outlined"
                      fullWidth
                      disabled={loading || isSubmitting}
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Copiar Chave PIX"
                              onClick={handleCopyPix}
                              edge="end"
                              size="small"
                              disabled={!field.value || loading || isSubmitting}
                              title="Copiar Chave PIX"
                            >
                              <ClipboardCopy size={16} />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}></Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="bank_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Banco (Código)"
                      variant="outlined"
                      fullWidth
                      disabled={loading || isSubmitting}
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="bank_agency"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Agência (sem dígito)"
                      variant="outlined"
                      fullWidth
                      disabled={loading || isSubmitting}
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="bank_account"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Conta Corrente (com dígito)"
                      variant="outlined"
                      fullWidth
                      disabled={loading || isSubmitting}
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading || isSubmitting}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                CANCELAR
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={loading || isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                disabled={loading || isSubmitting}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                {loading || isSubmitting ? 'SALVANDO...' : 'SALVAR'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelSave}
        onConfirm={handleSubmit(handleConfirmSave)}
        title={isEditMode ? "Confirmar Alteração" : "Confirmar Criação"}
        contentText={`Tem certeza que deseja salvar ${isEditMode ? 'as alterações nesta' : 'esta nova'} empresa?`}
        isLoading={loading || isSubmitting}
        confirmText={loading || isSubmitting ? 'Salvando...' : 'Confirmar'}
      />

      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default CompanyFormPage; 